import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { employeesApi } from '../services/employees';
import { assetsApi } from '../services/assets';
import { employeeTimeAllocationApi } from '../services/employeeTimeAllocation';

function Dashboard() {
  const { user, hasRole } = useAuth();
  const [employeesWithoutAssets, setEmployeesWithoutAssets] = useState([]);
  const [unallocatedAssetsCount, setUnallocatedAssetsCount] = useState(0);
  const [timeAllocationHistory, setTimeAllocationHistory] = useState([]);
  const [lastWeekMissingCount, setLastWeekMissingCount] = useState(0);
  const [thisWeekMissingCount, setThisWeekMissingCount] = useState(0);
  const [userAssets, setUserAssets] = useState([]);
  const [missingTimeAllocationWeeks, setMissingTimeAllocationWeeks] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUserAssets = useCallback(async () => {
    try {
      const response = await assetsApi.searchByEmployee(user.id);
      const data = response.data || response;
      setUserAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch user assets:', err);
      setUserAssets([]);
    }
  }, [user.id]);

  // Helper function to get week range (Monday to Sunday)
  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const monday = new Date(d);
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      start: monday,
      end: sunday
    };
  };

  const fetchTimeAllocationHistory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await employeeTimeAllocationApi.getByEmployee(user.id);
      // Get last 5 entries (already ordered by week_start_date DESC from backend)
      setTimeAllocationHistory(data.slice(0, 5));

      // Check for missing weeks (last week and this week) using date range
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);

      // Get week ranges (Monday to Sunday)
      const lastWeekRange = getWeekRange(lastWeek);
      const thisWeekRange = getWeekRange(today);

      // Convert existing dates to Date objects for range comparison
      const existingDates = data.map(entry => new Date(entry.week_start_date));

      // Check if any entry falls within the week range
      const hasEntryInRange = (startDate, endDate, dates) => {
        return dates.some(date => {
          const normalizedDate = new Date(date.toISOString().split('T')[0]);
          const start = new Date(startDate.toISOString().split('T')[0]);
          const end = new Date(endDate.toISOString().split('T')[0]);
          return normalizedDate >= start && normalizedDate <= end;
        });
      };

      const missing = [];

      if (!hasEntryInRange(lastWeekRange.start, lastWeekRange.end, existingDates)) {
        missing.push({ week: lastWeekRange.friday, label: 'Last Week' });
      }
      if (!hasEntryInRange(thisWeekRange.start, thisWeekRange.end, existingDates)) {
        missing.push({ week: thisWeekRange.friday, label: 'This Week' });
      }

      setMissingTimeAllocationWeeks(missing);
    } catch (err) {
      console.error('Failed to fetch time allocation history:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  const getFridayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -2 : 5);
    const friday = new Date(d.setDate(diff));
    return friday.toISOString().split('T')[0];
  };

  const fetchTimeAllocationStats = async () => {
    try {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      
      const lastWeekFriday = getFridayOfWeek(lastWeek);
      const thisWeekFriday = getFridayOfWeek(today);
      
      const [lastWeekMissing, thisWeekMissing] = await Promise.all([
        employeeTimeAllocationApi.getMissing(lastWeekFriday).catch(() => []),
        employeeTimeAllocationApi.getMissing(thisWeekFriday).catch(() => [])
      ]);
      
      setLastWeekMissingCount(lastWeekMissing.length);
      setThisWeekMissingCount(thisWeekMissing.length);
    } catch (err) {
      console.error('Failed to fetch time allocation stats:', err);
    }
  };

  useEffect(() => {
    if (hasRole('hr_head', 'super_admin')) {
      fetchEmployeesWithoutAssets();
      fetchAssetStats();
      fetchTimeAllocationStats();
    }
    if (user && !hasRole('hr_head', 'super_admin')) {
      fetchUserAssets();
      fetchTimeAllocationHistory();
    }
  }, [user, fetchUserAssets, fetchTimeAllocationHistory, hasRole]);

  const fetchEmployeesWithoutAssets = async () => {
    try {
      setLoading(true);
      const data = await employeesApi.getWithoutAssets();
      setEmployeesWithoutAssets(data);
    } catch (err) {
      console.error('Failed to fetch employees without assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetStats = async () => {
    try {
      const stats = await assetsApi.getStats();
      setUnallocatedAssetsCount(stats.unallocated);
    } catch (err) {
      console.error('Failed to fetch asset stats:', err);
    }
  };

  const formatWeekRange = (dateString) => {
    // Stored date is Friday of the week, calculate Monday to Sunday range
    const date = new Date(dateString);
    const day = date.getDay();
    const monday = new Date(date);
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  };

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Welcome, {user?.name}!</h3>
        </div>
      </div>

      {!hasRole('hr_head', 'super_admin') && userAssets.length === 0 && (
        <div className="card" style={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
          <div className="card-header">
            <h3 style={{ color: '#e65100' }}>No Assets Assigned</h3>
          </div>
          <p style={{ color: '#e65100' }}>You don't have any assets assigned to you yet.</p>
          <Link to={`/employees/${user.id}`} className="btn btn-primary btn-compact" style={{ marginTop: '12px' }}>
            + Assign Asset
          </Link>
        </div>
      )}

      {!hasRole('hr_head', 'super_admin') && missingTimeAllocationWeeks.length > 0 && (
        <div className="card" style={{ backgroundColor: '#ffebee', border: '1px solid #ef9a9a' }}>
          <div className="card-header">
            <h3 style={{ color: '#c62828' }}>Missing Time Allocation</h3>
          </div>
          <p style={{ color: '#c62800' }}>
            You have missing time allocation for: <strong>{missingTimeAllocationWeeks.map(w => w.label).join(', ')}</strong>
          </p>
          <Link to={`/employees/${user.id}`} className="btn btn-primary btn-compact" style={{ marginTop: '12px' }}>
            + Add Time Allocation
          </Link>
        </div>
      )}

      {!hasRole('hr_head', 'super_admin') && (
        <div className="card">
          <div className="card-header">
            <h3>Recent Time Allocation</h3>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : timeAllocationHistory.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>
              No time allocation data recorded yet. <Link to={`/employees/${user.id}`} style={{ color: '#1976d2' }}>Add your first time allocation entry</Link>.
            </p>
          ) : (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Time Allocation</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {timeAllocationHistory.map(timeAllocation => (
                    <tr key={timeAllocation.id}>
                      <td>{formatWeekRange(timeAllocation.week_start_date)}</td>
                      <td>
                        <span className="badge" style={{ 
                          backgroundColor: 
                            timeAllocation.time_allocation_percentage === '0-25%' ? '#f44336' :
                            timeAllocation.time_allocation_percentage === '25-50%' ? '#ff9800' :
                            timeAllocation.time_allocation_percentage === '50-75%' ? '#2196f3' :
                            timeAllocation.time_allocation_percentage === '75-100%' ? '#4caf50' : '#9e9e9e',
                          color: '#fff'
                        }}>
                          {timeAllocation.time_allocation_percentage}
                        </span>
                      </td>
                      <td>{timeAllocation.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to={`/employees/${user.id}`} className="btn btn-secondary btn-compact" style={{ marginTop: '16px' }}>
                View All Time Allocation History
              </Link>
            </div>
          )}
        </div>
      )}

      {hasRole('hr_head', 'super_admin') && (
        <>
          <div className="card">
            <div className="card-header">
              <h3>Summary</h3>
            </div>
            <div style={{ display: 'flex', gap: '24px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
                  Unallocated Assets
                </label>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#1976d2', marginTop: '8px' }}>
                  {unallocatedAssetsCount}
                </p>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
                  Last Week Missing Time Allocation
                </label>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f44336', marginTop: '8px' }}>
                  {lastWeekMissingCount}
                </p>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
                  This Week Missing Time Allocation
                </label>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff9800', marginTop: '8px' }}>
                  {thisWeekMissingCount}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Employees Without Assets</h3>
            </div>
            {loading ? (
              <p>Loading...</p>
            ) : employeesWithoutAssets.length === 0 ? (
              <p>All active employees have been assigned assets.</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Role</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeesWithoutAssets.map(emp => (
                      <tr key={emp.id}>
                        <td>
                          <Link to={`/employees/${emp.id}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                            {emp.name}
                          </Link>
                        </td>
                        <td>{emp.email}</td>
                        <td>{emp.department}</td>
                        <td>{emp.role}</td>
                        <td>
                          <Link to={`/assets/new`} state={{ assignTo: emp.id }} className="btn btn-primary btn-compact">
                            Assign Asset
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {hasRole('hr_head', 'super_admin') && (
        <div className="card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link to="/employees" className="btn btn-primary btn-compact">
              View Employees
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
